/* =============================================
   Collections Showcase — Two Panel + Mobile Slider
   File: assets/custom-collections-showcase.js

   Desktop (>989px):
   - Hover/click right list button → crossfade left panel
   - All panels position:absolute inside fixed-height container
     → no layout reflow → no background glitch

   Mobile/Tablet (≤989px):
   - Prev/next buttons step through panels
   - Dot pagination syncs with active panel
   - Touch swipe support (no external lib needed)
   ============================================= */

(function () {

  const MOBILE_BP = 989; /* px — matches typical tablet breakpoint */

  function isMobile() {
    return window.innerWidth <= MOBILE_BP;
  }

  function initCollectionsShowcase(section) {
    const display  = section.querySelector('.cs-display');
    const panels   = Array.from(section.querySelectorAll('.cs-display__panel'));
    const buttons  = Array.from(section.querySelectorAll('.cs-list__btn'));
    const dots     = Array.from(section.querySelectorAll('.cs-slider-dots__dot'));
    const prevBtn  = section.querySelector('.cs-slider-nav__btn--prev');
    const nextBtn  = section.querySelector('.cs-slider-nav__btn--next');

    if (!panels.length || !display) return;

    const total    = panels.length;
    const duration = (parseInt(section.dataset.duration, 10) || 350) / 1000;
    const ease     = 'power1.inOut';

    let activeIndex = 0;
    let isAnimating = false;

    /* ── Fix: set display to position:relative with explicit height ──
       All panels are position:absolute so the container never
       reflows during crossfade — this eliminates the bg glitch.
       Height is set to match the first panel's natural height. */
    function setDisplayHeight() {
      /* Temporarily make all panels relative to measure true height */
      panels.forEach((p) => {
        gsap.set(p, { position: 'absolute', top: 0, left: 0, width: '100%' });
      });
      gsap.set(panels[activeIndex], { position: 'relative' });

      /* Read height then lock it */
      const h = display.offsetHeight;
      display.style.height = h + 'px';

      /* Put active back to absolute now that height is locked */
      gsap.set(panels[activeIndex], { position: 'absolute' });
    }

    /* ── Init panel states ── */
    function initPanels() {
      panels.forEach((panel, i) => {
        gsap.set(panel, {
          opacity:  i === 0 ? 1 : 0,
          position: 'absolute',
          top:      0,
          left:     0,
          width:    '100%',
          zIndex:   i === 0 ? 1 : 0,
        });
      });
    }

    /* ── Crossfade to a panel ── */
    function switchTo(index, skipAnimation) {
      if (index === activeIndex && !skipAnimation) return;
      if (isAnimating) return;
      isAnimating = true;

      const outPanel = panels[activeIndex];
      const inPanel  = panels[index];

      /* Sync desktop buttons */
      buttons.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === index);
        btn.setAttribute('aria-pressed', String(i === index));
      });

      /* Sync dots */
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', String(i === index));
      });

      if (skipAnimation) {
        gsap.set(outPanel, { opacity: 0, zIndex: 0 });
        gsap.set(inPanel,  { opacity: 1, zIndex: 1 });
        activeIndex = index;
        isAnimating = false;
        return;
      }

      /* Bring incoming panel on top but invisible */
      gsap.set(inPanel, { zIndex: 2, opacity: 0 });

      /* Fade out old */
      gsap.to(outPanel, {
        opacity:  0,
        duration: duration,
        ease,
        onComplete: () => {
          gsap.set(outPanel, { zIndex: 0 });
        },
      });

      /* Fade in new */
      gsap.to(inPanel, {
        opacity:  1,
        duration: duration,
        ease,
        onComplete: () => {
          gsap.set(inPanel, { zIndex: 1 });
          isAnimating = false;
        },
      });

      activeIndex = index;
    }

    /* ── Wrap index for circular navigation ── */
    function wrapIndex(i) {
      return ((i % total) + total) % total;
    }

    /* ── Desktop: hover/click list buttons ── */
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!isMobile()) switchTo(parseInt(btn.dataset.target, 10));
      });
      btn.addEventListener('click', () => {
        if (!isMobile()) switchTo(parseInt(btn.dataset.target, 10));
      });
    });

    /* ── Mobile: prev / next buttons ── */
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        switchTo(wrapIndex(activeIndex - 1));
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        switchTo(wrapIndex(activeIndex + 1));
      });
    }

    /* ── Mobile: dot navigation ── */
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        switchTo(parseInt(dot.dataset.dot, 10));
      });
    });

    /* ── Mobile: touch swipe ── */
    let touchStartX = 0;
    let touchStartY = 0;
    const SWIPE_THRESHOLD = 50; /* px */

    display.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    display.addEventListener('touchend', (e) => {
      if (!isMobile()) return;

      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;

      /* Only trigger if horizontal swipe dominates */
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dy) > Math.abs(dx)) return;

      if (dx < 0) {
        switchTo(wrapIndex(activeIndex + 1)); /* swipe left → next */
      } else {
        switchTo(wrapIndex(activeIndex - 1)); /* swipe right → prev */
      }
    }, { passive: true });

    /* ── Recalculate height on resize ── */
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        display.style.height = '';
        setDisplayHeight();
      }, 150);
    });

    /* ── Init ── */
    initPanels();
    setDisplayHeight();
  }

  function init() {
    if (typeof gsap === 'undefined') {
      setTimeout(init, 50);
      return;
    }
    document.querySelectorAll('.collections-showcase').forEach(initCollectionsShowcase);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
